/**
 * titleInput — the pure rule behind the Title front-door's hit-testing mode.
 *
 * WHY THIS EXISTS (the "Continue opened New Game" bug). On the front-door MENU view the
 * two options (Continue / New game) are adjacent rows whose hit zones share a boundary.
 * With pass-through hit-testing (`scene.input.topOnly = false`) a tap on (or near) that
 * shared edge fires BOTH rows' handlers — so tapping "Continue" ALSO fired "New game", and
 * `goTo('NewGame')` won the fade-then-start race, dropping the player into character
 * creation instead of the save picker.
 *
 * The fix is to make the Title's hit-testing mode a pure function of which view is on
 * screen, and to OWN that flag explicitly rather than inherit whatever a child left behind:
 * the MENU view wants TOP-ONLY (each tap hits exactly the one row under it, so Continue is
 * hit cleanly), while the PICKER view opts into pass-through (belt-and-suspenders so the
 * {@link ScrollList}'s save rows + Back button all stay reliably tappable). The scene
 * applies this on entry, on every view swap, and on re-entry — so the picker's pass-through
 * can never strand the menu in a mode that co-fires the two options.
 *
 * (ScrollList itself no longer flips the scene-wide `topOnly` flag — it tracks its
 * touch-drag at the scene input level — so the Title's applyHitMode is the ONLY thing that
 * sets `topOnly` here, and it keeps the two views' modes from ever leaking into each other.)
 *
 * Kept as a pure function (no Phaser) so the rule is trivially unit-testable, mirroring
 * townTapGuard / pickerNav / savePicker.
 */

/** Which front-door view is on screen. */
export type TitleView = 'menu' | 'picker';

/**
 * The `scene.input.topOnly` the Title must run with for `view`.
 *
 * @returns `true` for the MENU view — top-only hit-testing so a tap resolves to exactly
 *          the one option under it (Continue never co-fires New game). `false` for the
 *          PICKER view — pass-through so every save row + the Back button stays reliably
 *          tappable (belt-and-suspenders while the ScrollList's rows are stacked).
 */
export function titleTopOnly(view: TitleView): boolean {
  return view === 'menu';
}
